import { DestroyRef, Directive, inject, input, output, signal } from '@angular/core';

const LONG_PRESS_DELAY_MS = 500;
// a finger drifts a few pixels while held; past that the gesture is a scroll, not a press
const LONG_PRESS_SLOP_PX = 10;

/**
 * Emits `press` when the element is held still for `delay`. Pointer events cover touch, mouse and pen in one path,
 * so no Ionic gesture is needed.
 */
@Directive({
  selector: '[longPress]',
  exportAs: 'longPress',
  host: {
    '(pointerdown)': 'startPress($event)',
    '(pointermove)': 'cancelWhenMoved($event)',
    '(pointerup)': 'cancelPress()',
    '(pointercancel)': 'cancelPress()',
    '(pointerleave)': 'cancelPress()',
  },
})
export class LongPressDirective {
  readonly delay = input(LONG_PRESS_DELAY_MS);

  readonly press = output<PointerEvent>();

  private timer: ReturnType<typeof setTimeout> | undefined;
  private origin: { x: number; y: number } | undefined;
  private readonly pressEmitted = signal(false);
  private scrollTargets: EventTarget[] = [];
  private readonly cancelOnScroll = () => this.cancelPress();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancelPress());
  }

  startPress(event: PointerEvent) {
    this.cancelPress();
    this.pressEmitted.set(false);
    this.origin = { x: event.clientX, y: event.clientY };
    // scroll is not composed, so it never leaves ion-content's shadow root; listen on the scrollers in the path itself
    this.scrollTargets = event.composedPath();
    this.scrollTargets.forEach((target) => target.addEventListener('scroll', this.cancelOnScroll));
    this.timer = setTimeout(() => {
      this.pressEmitted.set(true);
      this.press.emit(event);
    }, this.delay());
  }

  cancelWhenMoved(event: PointerEvent) {
    const origin = this.origin;
    if (!origin) {
      return;
    }

    const movedTooFar =
      Math.abs(event.clientX - origin.x) > LONG_PRESS_SLOP_PX ||
      Math.abs(event.clientY - origin.y) > LONG_PRESS_SLOP_PX;

    if (movedTooFar) {
      this.cancelPress();
    }
  }

  cancelPress() {
    clearTimeout(this.timer);
    this.origin = undefined;
    this.scrollTargets.forEach((target) => target.removeEventListener('scroll', this.cancelOnScroll));
    this.scrollTargets = [];
  }
}
